/**
 * UN-DOLLAR RENTFLOW — AUTHENTICATION CONTEXT
 * 
 * React Context for Firebase authentication with role-based access
 * Supports portal-specific RBAC (Property Owner, Property Manager, Assistant, etc.)
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { auth, db } from '../services/firebase';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';

// BUILD TIMESTAMP: 2025-12-09 5:00 PM - Window debug variables added
const BUILD_TIME = '2025-12-09_17:00_UTC';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User
} from 'firebase/auth';
import {
  PortalRole,
  AdminRole,
  UserRole,
  PortalPermission,
  PortalUserProfile,
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
  getPermissionsForRole,
  isAdminRole,
  isPortalRole,
} from '../types/permissions';

interface UserProfile extends PortalUserProfile {
  // For type safety - allows both admin and portal roles
  role: UserRole;
}

interface AuthContextType {
  user: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isPropertyOwner: boolean;
  isPropertyManager: boolean;
  hasPermission: (permission: PortalPermission) => boolean;
  hasAnyPermission: (permissions: PortalPermission[]) => boolean;
  hasAllPermissions: (permissions: PortalPermission[]) => boolean;
  canAccessProperty: (propertyId: string) => boolean;
  getLandlordsForUser: () => string[]; // Get all landlord IDs for current user
  getPropertiesForLandlord: (landlordId: string) => string[]; // Get properties for specific landlord
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      
      if (user) {
        // Fetch user profile from Firestore to get role and permissions
        try {
          // Try landlords collection first, then fall back to users collection (for test accounts)
          let userDoc = await getDoc(doc(db, 'landlords', user.uid));
          let collectionUsed = 'landlords';
          
          if (!userDoc.exists()) {
            userDoc = await getDoc(doc(db, 'users', user.uid));
            collectionUsed = 'users';
            console.log('[AuthContext] Fallback to users collection');
          }
          
          if (userDoc.exists()) {
            const data = userDoc.data();
            const role = (data.role || 'property_owner') as UserRole;
            
            console.log('[AuthContext] ✅ User document found in', collectionUsed);
            console.log('[AuthContext] Role from Firestore:', role);
            console.log('[AuthContext] Full data:', { email: user.email, collection: collectionUsed, role, data });
            
            // Get assigned properties for property managers, assistants, and maintenance coordinators
            let assignedProperties: string[] | undefined;
            let ownerIds: string[] | undefined;
            const propertyData = new Map<string, { landlordId: string }>();
            
            if (role === 'property_manager' || role === 'assistant' || role === 'maintenance_coordinator') {
              try {
                const assignmentsSnapshot = await getDocs(
                  query(
                    collection(db, 'propertyAssignments'),
                    where('userId', '==', user.uid)
                  )
                );
                assignedProperties = assignmentsSnapshot.docs.map(doc => doc.data().propertyId);
                
                // Fetch property details to get landlord IDs
                if (assignedProperties && assignedProperties.length > 0) {
                  const landlordIdSet = new Set<string>();
                  
                  for (const propertyId of assignedProperties) {
                    try {
                      const propertyDoc = await getDoc(doc(db, 'properties', propertyId));
                      if (propertyDoc.exists()) {
                        const propertyData_item = propertyDoc.data();
                        const landlordId = propertyData_item?.landlordId;
                        
                        if (landlordId) {
                          landlordIdSet.add(landlordId);
                          propertyData.set(propertyId, { landlordId });
                        }
                      }
                    } catch (error) {
                      console.warn(`Failed to fetch property ${propertyId}:`, error);
                    }
                  }
                  
                  ownerIds = Array.from(landlordIdSet);
                }
              } catch (error) {
                // Permission error or other issue fetching assignments - log but don't fail
                console.warn('[AuthContext] ⚠️ Could not fetch property assignments:', error);
                console.warn('[AuthContext] ⚠️ Continuing with role:', role);
                // Continue with the profile using the role we already found
              }
            }
            
            console.log('[AuthContext] 🔧 Setting userProfile with role:', role);
            const profileData = {
              uid: user.uid,
              email: user.email || '',
              role,
              name: data.name,
              permissions: getPermissionsForRole(role),
              assignedProperties,
              ownerId: data.ownerId, // Primary owner for backward compatibility
              ownerIds, // All owners this user manages properties for
              propertyData, // Cache of property-to-landlord mapping
            };
            setUserProfile(profileData);
            
            // Store in window for debugging - can check via console: window.debugAuthRole
            (window as any).debugAuthRole = role;
            (window as any).debugAuthProfile = profileData;
            (window as any).buildTime = BUILD_TIME;
            
            // Log whether this is an admin or portal role
            const permissionsCount = getPermissionsForRole(role).length;
            console.log('[AuthContext] ✅ Profile set successfully:', { 
              role, 
              isAdmin: isAdminRole(role),
              isPortalRole: isPortalRole(role),
              permissionsCount,
              assignedPropertiesCount: assignedProperties?.length || 0,
              note: 'Check window.debugAuthRole and window.debugAuthProfile in console'
            });
          } else {
            // Default to property_owner role if no profile exists (backward compatibility)
            console.warn('[AuthContext] ⚠️ No user document found! Using default role: property_owner');
            const defaultRole: PortalRole = 'property_owner';
            setUserProfile({
              uid: user.uid,
              email: user.email || '',
              role: defaultRole,
              permissions: getPermissionsForRole(defaultRole),
            });
          }
        } catch (error) {
          console.error('[AuthContext] ❌ CRITICAL ERROR in profile fetch:', error);
          console.error('[AuthContext] Error details:', { message: (error as Error)?.message, stack: (error as Error)?.stack });
          const defaultRole: PortalRole = 'property_owner';
          console.log('[AuthContext] ❌ Falling back to default role:', defaultRole);
          setUserProfile({
            uid: user.uid,
            email: user.email || '',
            role: defaultRole,
            permissions: getPermissionsForRole(defaultRole),
          });
        }
      } else {
        setUserProfile(null);
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Role checks - Use the isAdminRole helper to check for any admin role
  const isAdmin = userProfile ? isAdminRole(userProfile.role) : false;
  const isSuperAdmin = userProfile?.role === 'superadmin';
  const isPropertyOwner = userProfile?.role === 'property_owner' || (userProfile?.role as any) === 'landlord';
  const isPropertyManager = userProfile?.role === 'property_manager';

  // Permission checking functions
  const checkPermission = (permission: PortalPermission): boolean => {
    if (!userProfile) return false;
    
    // Admin roles have all permissions
    if (isAdmin || isSuperAdmin) return true;
    
    let role = userProfile.role as PortalRole;
    // Backward compatibility: treat 'landlord' as 'property_owner'
    if ((role as any) === 'landlord') {
      role = 'property_owner';
    }
    return hasPermission(role, permission);
  };

  const checkAnyPermission = (permissions: PortalPermission[]): boolean => {
    if (!userProfile) return false;
    
    // Admin roles have all permissions
    if (isAdmin || isSuperAdmin) return true;
    
    let role = userProfile.role as PortalRole;
    if ((role as any) === 'landlord') {
      role = 'property_owner';
    }
    return hasAnyPermission(role, permissions);
  };

  const checkAllPermissions = (permissions: PortalPermission[]): boolean => {
    if (!userProfile) return false;
    
    // Admin roles have all permissions
    if (isAdmin || isSuperAdmin) return true;
    
    let role = userProfile.role as PortalRole;
    if ((role as any) === 'landlord') {
      role = 'property_owner';
    }
    return hasAllPermissions(role, permissions);
  };

  const canAccessProperty = (propertyId: string): boolean => {
    if (!userProfile) return false;
    
    // Property owners can access all their properties
    if (isPropertyOwner || isAdmin || isSuperAdmin) return true;
    
    // Property managers, assistants, maintenance can only access assigned properties
    if (userProfile.assignedProperties) {
      return userProfile.assignedProperties.includes(propertyId);
    }
    
    return false;
  };

  // Get all landlords for the current user
  const getLandlordsForUser = (): string[] => {
    if (!userProfile) return [];
    
    // For property owners, they ARE the landlord
    if (isPropertyOwner) {
      return [userProfile.uid];
    }
    
    // For property managers, get all landlords from assigned properties
    if (userProfile.ownerIds && userProfile.ownerIds.length > 0) {
      return userProfile.ownerIds;
    }
    
    // Fallback to primary owner if available
    if (userProfile.ownerId) {
      return [userProfile.ownerId];
    }
    
    return [];
  };

  // Get properties for a specific landlord that the current user can access
  const getPropertiesForLandlord = (landlordId: string): string[] => {
    if (!userProfile) return [];
    
    // If user is the property owner, they can access all their properties
    if (isPropertyOwner && userProfile.uid === landlordId) {
      return userProfile.assignedProperties || [];
    }
    
    // If property manager, filter assigned properties by landlord
    if (userProfile.assignedProperties && userProfile.propertyData) {
      return userProfile.assignedProperties.filter(propertyId => {
        const propData = userProfile.propertyData?.get(propertyId);
        return propData?.landlordId === landlordId;
      });
    }
    
    return [];
  };

  const signIn = async (email: string, password: string) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to sign in';
      throw new Error(message);
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      await createUserWithEmailAndPassword(auth, email, password);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to sign up';
      throw new Error(message);
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to sign out';
      throw new Error(message);
    }
  };

  return (
    <AuthContext.Provider 
      value={{ 
        user, 
        userProfile, 
        loading, 
        isAdmin, 
        isSuperAdmin, 
        isPropertyOwner,
        isPropertyManager,
        hasPermission: checkPermission,
        hasAnyPermission: checkAnyPermission,
        hasAllPermissions: checkAllPermissions,
        canAccessProperty,
        getLandlordsForUser,
        getPropertiesForLandlord,
        signIn, 
        signUp, 
        signOut 
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
